/* eslint-disable react/no-danger */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import axios from 'axios';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import LoadIcon from '@material-ui/icons/Refresh';
import ErrorComponent from '../ErrorComponent';
import handleMenu from './menu';
import { getFirstChild } from './domUtils';
import './DocComponent.scss';

const LoadingIcon = () => (
  <LoadIcon
    className="grb-doc-component-loading-icon"
    style={{
      animation: 'Rotate 5s infinite linear',
      fontSize: 40,
    }}
  />
);

const processDocContent = (doccontent) => {
  const index = doccontent.indexOf('<main>');
  let content;
  if (index >= 0) {
    const last = doccontent.lastIndexOf('</main>');
    if (last > 0) {
      content = doccontent.substring(index, last + 7);
    } else {
      content = doccontent.substring(index);
    }
  } else {
    content = doccontent.substring(0);
  }

  let onLoadFctName;
  let imageCount = 0;
  // Set onload callback on all img elements
  content = content.replaceAll('<img ', () => {
    if (!onLoadFctName) {
      onLoadFctName = `onLoad${new Date().getTime()}`;
    }
    imageCount += 1;
    return `<img onload="${onLoadFctName}()" `;
  });
  return {
    content,
    onLoadFctName,
    imageCount,
  };
};

const DocComponent = (props) => {
  const { route, location, authService } = props;
  const configuration = route && route.getProperty('configuration') || {};
  const [{ loading, content, imagesLoaded, error }, setPage] = useState({ loading: true });
  const contentRef = useRef();
  const onLoadImageFctName = useRef(null);
  const navRef = useRef();

  const handleUpdateLocation = useCallback((loc) => {
    window.location.replace(loc);
  }, []);
  useEffect(() => {
    setPage({ loading: true });
    const clearOnLoad = () => {
      if (onLoadImageFctName.current) {
        delete window[onLoadImageFctName.current];
        onLoadImageFctName.current = null;
      }
    };
    clearOnLoad();
    axios.get(configuration.url)
    .then((res) => {
      const {
        content: pcontent,
        onLoadFctName,
        imageCount,
      } = processDocContent(res.data);
      const innerHTML = {
        __html: pcontent,
      };
      if (imageCount) {
        onLoadImageFctName.current = onLoadFctName;
        let loadedImgs = 0;
        window[onLoadFctName] = () => {
          loadedImgs += 1;
          if (loadedImgs === imageCount) {
            setPage({ content: innerHTML, imagesLoaded: true });
          }
        };
      }
      setPage({ content: innerHTML, imagesLoaded: !imageCount });
    })
    .catch((e) => {
      setPage({ error: e });
    });
    return clearOnLoad;
  }, [configuration]);

  // Update menu if necessary
  useEffect(() => {
    if (imagesLoaded) {
      // Init the menu once the page is loaded with its images
      // Otherwise, the yscroll position calculated from location #section could
      // point to a different section after the image are loaded.
      // See first point of http://github.compute.gurobi.com/Gurobi/grbrs-ng/issues/1072
      const mainElt = getFirstChild(contentRef.current);
      handleMenu({
        menuElt: navRef.current,
        mainElt,
        location,
        onUpdateLocation: handleUpdateLocation,
      });
    }
  }, [content, imagesLoaded]);

  if (loading) {
    return (
      <div className="grb-doc-component">
        <div className="grb-doc-component-loading">
          <LoadingIcon />
          <Typography variant="h5">{`Loading ${configuration.title}`}</Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grb-doc-component">
        <Paper className="grb-doc-component-error">
          <ErrorComponent title={`Error loading ${configuration.title}`} error={error} />
        </Paper>
      </div>
    );
  }

  return (
    <div className={classnames(
        'grb-doc-component',
        authService && authService.getRole && `grb-role-${authService.getRole()}`,
      )}>
      <div
        dangerouslySetInnerHTML={content}
        className="grb-doc-component-content"
        ref={contentRef}
      />
      <nav ref={navRef} className="grb-doc-component-nav" />
    </div>
  );
};

DocComponent.propTypes = {
  route: PropTypes.shape({
    getProperty: PropTypes.func,
  }),
  routeService: PropTypes.shape({
    getHomeURL: PropTypes.func,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  configuration: PropTypes.shape({
    jsonConfig: PropTypes.shape({
      doc: PropTypes.shape({
        homeButton: PropTypes.bool,
      }),
    }),
  }),
  authService: PropTypes.shape({
    getRole: PropTypes.func,
  }),
};

DocComponent.defaultProps = {
  route: null,
  configuration: {},
  authService: undefined,
};

export default DocComponent;
